import { useDispatch, useSelector } from 'react-redux';

import { getSyncPairs } from '~/api/evisync';
import { getStorageIntegrations } from '~/common/constants/integrations';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { ManageSyncPairStageType } from '~/enums/ManageSyncPairStageType';
import { OnboardingIdType } from '~/features/onboarding/OnboardingIdType';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { useIntl } from 'react-intl';
import { api } from '~/redux/api/createApi';
import { slices } from '~/redux/slices';
import { useRouting } from '~/routing/useRouting';
import ChipList from '~/ui/components/ChipList/ChipList';
import FlexLayout from '~/ui/components/FlexLayout/FlexLayout';
import Folder from '~/ui/components/Folder/Folder';
import Text from '~/ui/components/Text/Text';
import { fileTypesToChips } from '~/utils/fileTypesToChips';

import { resetSyncPairAuthData } from '../../util';
import Footer from '../Footer';
import SummaryRow from './SummaryRow';

function Summary() {
  const intl = useIntl();
  const dispatch = useDispatch();
  const activeSyncPair = useSelector(
    slices.integrations.selectors.selectActiveSyncPair,
  );

  const {
    allowedSyncFileTypes,
    name,
    provider,
    evisortFolder,
    providerFolder,
  } = activeSyncPair;

  const { params } = useRouting();

  const { clientId } = params;

  const enableSkipEmptyFolders = useFlag(FlagType.SkipEmptyFolders);

  const { toast } = useToast();

  const [createSyncPair, { isLoading: isCreating }] =
    api.endpoints.createSyncPair.useMutation();

  const handleCreateSyncPair = () => {
    createSyncPair(activeSyncPair)
      .unwrap()
      .then(() => {
        getSyncPairs({ clientId }).then((pairs) =>
          dispatch(slices.integrations.actions.setSyncPairs(pairs)),
        );
        dispatch(
          slices.integrations.actions.setManageSyncPairStage(
            ManageSyncPairStageType.None,
          ),
        );
        dispatch(slices.integrations.actions.setActiveSyncPair(null));
        toast({
          message: intl.formatMessage(
            {
              id: 'Summary.createSyncPairSuccess',
              defaultMessage: '{name} sync pair has been created.',
              description:
                'Success message when sync pair is created. Example: name = "Document Name"',
            },
            { name: activeSyncPair.name },
          ),
          status: 'success',
        });
      })
      .catch((error) => {
        const defaultErrorMessage = intl.formatMessage({
          id: 'Summary.createSyncPairError',
          defaultMessage: 'Unable to create sync pair, please try again.',
          description: 'Default error message when creating sync pair fails',
        });
        const errorMessage = error.response.data[0] ?? defaultErrorMessage;
        toast({
          message: errorMessage,
          status: 'danger',
        });
      });
  };

  const handleGoToStage = (stage) => () => {
    dispatch(slices.integrations.actions.setManageSyncPairStage(stage));

    const authRequiredStages = [
      ManageSyncPairStageType.Provider,
      ManageSyncPairStageType.Authentication,
    ];

    if (authRequiredStages.includes(stage)) {
      dispatch(
        slices.integrations.actions.setActiveSyncPair(
          resetSyncPairAuthData(activeSyncPair),
        ),
      );
    }
  };

  const handleBack = () => {
    dispatch(
      slices.integrations.actions.setManageSyncPairStage(
        ManageSyncPairStageType.Folders,
      ),
    );
  };

  return (
    <FlexLayout
      alignItems="stretch"
      flexDirection="column"
      sx={{ width: '100%' }}
    >
      <SummaryRow
        title={intl.formatMessage({
          id: 'Summary.providerTitle',
          defaultMessage: 'Provider',
          description: 'Title for provider section',
        })}
        onChange={handleGoToStage(ManageSyncPairStageType.Provider)}
      >
        <Text color="gray-900" variant="s-dense">
          {name}
        </Text>
      </SummaryRow>
      <SummaryRow
        title={intl.formatMessage({
          id: 'Summary.authenticationTitle',
          defaultMessage: 'Authentication',
          description: 'Title for authentication section',
        })}
        onChange={handleGoToStage(ManageSyncPairStageType.Authentication)}
      >
        <Text color="gray-900" variant="s-dense">
          {intl.formatMessage(
            {
              id: 'Summary.accountLabel',
              defaultMessage: '{providerName} Account',
              description:
                'Account label with provider name. Example: providerName = "Document Name"',
            },
            { providerName: getStorageIntegrations()[provider].name },
          )}
        </Text>
      </SummaryRow>
      <SummaryRow
        title={intl.formatMessage({
          id: 'Summary.foldersTitle',
          defaultMessage: 'Folders',
          description: 'Title for folders section',
        })}
        onChange={handleGoToStage(ManageSyncPairStageType.Folders)}
      >
        <FlexLayout flexDirection="column" space={4}>
          <Folder
            folder={providerFolder}
            subtext={
              enableSkipEmptyFolders && !!activeSyncPair?.skipEmptyFolders
                ? intl.formatMessage({
                    id: 'Summary.skipEmptyFoldersSubtext',
                    defaultMessage: 'Skip empty folders',
                    description: 'Subtext for skip empty folders option',
                  })
                : ''
            }
          />
          <Folder folder={evisortFolder} />
        </FlexLayout>
      </SummaryRow>
      <SummaryRow
        title={intl.formatMessage({
          id: 'Summary.fileTypesTitle',
          defaultMessage: 'File types',
          description: 'Title for file types section',
        })}
        onChange={handleGoToStage(ManageSyncPairStageType.Folders)}
      >
        <ChipList chips={fileTypesToChips(allowedSyncFileTypes)} limit={100} />
      </SummaryRow>
      <Footer
        leftAction={{
          text: intl.formatMessage({
            id: 'Summary.backButton',
            defaultMessage: 'Back',
            description: 'Back button text',
          }),
          onClick: handleBack,
        }}
        rightAction={{
          id: OnboardingIdType.ConfirmSyncPair,
          isLoading: isCreating,
          text: intl.formatMessage({
            id: 'Summary.setupSyncPairButton',
            defaultMessage: 'Set up Sync Pair',
            description: 'Set up sync pair button text',
          }),
          onClick: handleCreateSyncPair,
        }}
      />
    </FlexLayout>
  );
}

export default Summary;
